import React, { useEffect, useState } from 'react';
import httpListService from '../../services/httpListService';

export interface HttpListProps {
  /** Root-relative or absolute URL the client GETs with the signed-in user token. */
  url: string;
  /** Top-level property on the JSON body that holds the array. */
  items: string;
  /** Field on each object shown as the row label. */
  label: string;
  /** Optional field shown under the label. */
  detail?: string;
  /** Text shown when the array is empty. */
  empty?: string;
}

interface ListRow {
  key: string;
  label: string;
  detail: string;
}

type ListStatus = 'loading' | 'ready' | 'error';

function readField(row: object, field: string): string {
  if (!Object.prototype.hasOwnProperty.call(row, field)) {
    return '';
  }
  const value = (row as Record<string, unknown>)[field];
  return typeof value === 'string' ? value.trim() : '';
}

function readRows(body: unknown, itemsKey: string, labelKey: string, detailKey?: string): ListRow[] {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return [];
  }
  if (!Object.prototype.hasOwnProperty.call(body, itemsKey)) {
    return [];
  }
  const items = (body as Record<string, unknown>)[itemsKey];
  if (!Array.isArray(items)) {
    return [];
  }
  const rows: ListRow[] = [];
  items.forEach((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return;
    }
    const label = readField(item, labelKey);
    if (!label) {
      return;
    }
    const detail = detailKey ? readField(item, detailKey) : '';
    rows.push({
      key: detail ? `${detail}:${index}` : `${label}:${index}`,
      label,
      detail,
    });
  });
  return rows;
}

/**
 * Read-only list from an authenticated GET. Renders labels only.
 */
const HttpList: React.FC<HttpListProps> = ({ url, items, label, detail, empty }) => {
  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<ListRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    httpListService
      .getJson(url)
      .then((body) => {
        if (cancelled) {
          return;
        }
        setRows(readRows(body, items, label, detail));
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url, items, label, detail]);

  if (status === 'loading') {
    return <p data-testid="http-list-loading">Loading…</p>;
  }

  if (status === 'error') {
    return <p data-testid="http-list-error">Could not load this list.</p>;
  }

  if (rows.length === 0) {
    return <p data-testid="http-list-empty">{empty ?? ''}</p>;
  }

  return (
    <ul data-testid="http-list">
      {rows.map((row) => (
        <li key={row.key} data-testid="http-list-row">
          <div data-testid="http-list-label">{row.label}</div>
          {row.detail ? <div data-testid="http-list-detail">{row.detail}</div> : null}
        </li>
      ))}
    </ul>
  );
};

export default HttpList;
